import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import {
  getTrendingPlacesRegions,
  getTrendingPlacesCategories,
  getTrendingPlacesByRegion,
  getTrendingPlaceImageUrl
} from '../api/client';
import {
  FaHiking,
  FaUtensils,
  FaTheaterMasks,
  FaSkiing,
  FaSpa,
  FaShoppingBag,
  FaCocktail,
  FaLandmark,
  FaTree,
  FaCamera,
  FaUmbrellaBeach,
  FaUniversity,
  FaPrayingHands,
  FaMapMarkerAlt,
  FaPlane,
  FaCar,
  FaTrain,
  FaBus,
  FaWalking,
  FaBiking,
  FaCalendarAlt,
  FaMountain,
  FaCity,
  FaFire,
  FaTimes,
  FaChevronRight,
  FaExclamationTriangle,
  FaRocket,
  FaDirections,
  FaLock,
  FaInfoCircle,
  FaPlus,
  FaMicrophone
} from 'react-icons/fa';
import { VoiceInputInline } from './voice/VoiceInput';
import { useVoiceSupport } from './voice/VoiceProvider';
import { FaGlobe, FaChevronLeft, FaRuler } from 'react-icons/fa';
import LocationInput from './LocationInput';
import PageGuide from './PageGuide';
import { getDistanceUnit, getDistanceRadius } from '../utils/distanceUtils';

// Category-specific fallback images for when place images fail to load
const CATEGORY_FALLBACK_IMAGES = {
  "Mountains": "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?w=400&h=300&fit=crop",
  "Beaches": "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?w=400&h=300&fit=crop",
  "Cities": "https://images.unsplash.com/photo-1480714378408-67cf0d13bc1b?w=400&h=300&fit=crop",
  "Nature": "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=400&h=300&fit=crop",
  "Islands": "https://images.unsplash.com/photo-1559128010-7c1ad6e1b6a5?w=400&h=300&fit=crop",
  "Deserts": "https://images.unsplash.com/photo-1509316785289-025f5b846b35?w=400&h=300&fit=crop",
  "Lakes": "https://images.unsplash.com/photo-1439066615861-d1af74d74000?w=400&h=300&fit=crop",
  "Historical": "https://images.unsplash.com/photo-1552832230-c0197dd311b5?w=400&h=300&fit=crop",
  "Adventure": "https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=400&h=300&fit=crop",
  "Cultural": "https://images.unsplash.com/photo-1493976040374-85c8e12f0c0e?w=400&h=300&fit=crop",
  "Coastal": "https://images.unsplash.com/photo-1519046904884-53103b34b206?w=400&h=300&fit=crop",
  "Wildlife": "https://images.unsplash.com/photo-1474511320723-9a56873571b7?w=400&h=300&fit=crop",
  "Winter": "https://images.unsplash.com/photo-1483921020237-2ff51e8e4b22?w=400&h=300&fit=crop",
  "Tropical": "https://images.unsplash.com/photo-1516815231560-8f41ec531527?w=400&h=300&fit=crop",
};
const DEFAULT_FALLBACK_IMAGE = "https://images.unsplash.com/photo-1488646953014-85cb44e25828?w=400&h=300&fit=crop";

// Helper function to get category-specific fallback image
const getCategoryFallbackImage = (category) => {
  if (!category) return DEFAULT_FALLBACK_IMAGE;
  // Try exact match
  if (CATEGORY_FALLBACK_IMAGES[category]) return CATEGORY_FALLBACK_IMAGES[category];
  // Try case-insensitive match
  const categoryLower = category.toLowerCase();
  for (const [key, url] of Object.entries(CATEGORY_FALLBACK_IMAGES)) {
    if (key.toLowerCase() === categoryLower) return url;
    // Partial match (e.g., "Mountain Adventures" matches "Mountains")
    if (key.toLowerCase().includes(categoryLower) || categoryLower.includes(key.toLowerCase())) return url;
  }
  return DEFAULT_FALLBACK_IMAGE;
};

// Category UI configuration - maps category names to icons and colors
const categoryConfig = {
  'Mountains': {
    icon: FaMountain,
    color: 'from-emerald-500 to-teal-600',
    interests: ['hiking', 'nature', 'adventure', 'photography']
  },
  'Beaches': {
    icon: FaUmbrellaBeach,
    color: 'from-cyan-500 to-blue-600',
    interests: ['beaches', 'relaxation', 'nature', 'photography']
  },
  'Cities': {
    icon: FaCity,
    color: 'from-purple-500 to-indigo-600',
    interests: ['culture', 'foodie', 'shopping', 'nightlife', 'museums']
  },
  'Nature': {
    icon: FaTree,
    color: 'from-green-500 to-emerald-600',
    interests: ['nature', 'hiking', 'adventure', 'photography']
  },
  // Additional categories that can be configured via admin
  'Adventure': {
    icon: FaHiking,
    color: 'from-orange-500 to-red-600',
    interests: ['adventure', 'hiking', 'nature', 'sports']
  },
  'Culture': {
    icon: FaLandmark,
    color: 'from-amber-500 to-yellow-600',
    interests: ['culture', 'museums', 'history', 'architecture']
  },
  'Relaxation': {
    icon: FaSpa,
    color: 'from-pink-500 to-rose-600',
    interests: ['relaxation', 'spa', 'wellness', 'beaches']
  },
  'Food': {
    icon: FaUtensils,
    color: 'from-red-500 to-orange-600',
    interests: ['foodie', 'culture', 'local experiences']
  }
};

// Default fallback for unknown categories
const defaultCategoryConfig = {
  icon: FaMapMarkerAlt,
  color: 'from-gray-500 to-slate-600',
  interests: ['travel', 'exploration']
};

// Helper to get category config with fallback
const getCategoryConfig = (name) => categoryConfig[name] || defaultCategoryConfig;

// Empty initial state — trending destinations are 100% AI-generated from the backend
const emptyTrendingDestinations = {
  Mountains: [],
  Beaches: [],
  Cities: [],
  Nature: []
};

const interestOptions = [
  { value: 'hiking', label: 'Hiking', icon: FaHiking },
  { value: 'foodie', label: 'Foodie', icon: FaUtensils },
  { value: 'culture', label: 'Culture', icon: FaTheaterMasks },
  { value: 'adventure', label: 'Adventure', icon: FaSkiing },
  { value: 'relaxation', label: 'Relaxation', icon: FaSpa },
  { value: 'shopping', label: 'Shopping', icon: FaShoppingBag },
  { value: 'nightlife', label: 'Nightlife', icon: FaCocktail },
  { value: 'history', label: 'History', icon: FaLandmark },
  { value: 'nature', label: 'Nature', icon: FaTree },
  { value: 'photography', label: 'Photography', icon: FaCamera },
  { value: 'beaches', label: 'Beaches', icon: FaUmbrellaBeach },
  { value: 'museums', label: 'Museums', icon: FaUniversity },
  { value: 'religious', label: 'Religious', icon: FaPrayingHands },
];

const ItineraryForm = ({ onSubmit, loading, initialData = null, subscriptionLimitError = null, onViewPricing = null }) => {
  const [startLocation, setStartLocation] = useState(initialData?.start_location || '');
  const [endLocation, setEndLocation] = useState(initialData?.end_location || '');
  const [selectedInterests, setSelectedInterests] = useState(initialData?.interests || []);
  const [foodPreference, setFoodPreference] = useState(initialData?.food_preference || '');
  const [startDate, setStartDate] = useState(initialData?.start_date || '');
  const [endDate, setEndDate] = useState(initialData?.end_date || '');
  const [transportationMode, setTransportationMode] = useState(initialData?.transportation_mode || '');
  const [avoidTollRoads, setAvoidTollRoads] = useState(initialData?.avoid_toll_roads || false);
  const [preferTollRoads, setPreferTollRoads] = useState(initialData?.prefer_toll_roads || false);
  const [scenicRoads, setScenicRoads] = useState(initialData?.scenic_roads || false);
  const [waypoints, setWaypoints] = useState([{ city: '', days: '', arrivalDate: '', departureDate: '' }]);
  const [distanceRadius, setDistanceRadius] = useState(initialData?.distance_radius || 50);
  const distanceUnit = getDistanceUnit();
  const [errors, setErrors] = useState({});
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [trendingDestinations, setTrendingDestinations] = useState(emptyTrendingDestinations);
  const [trendingLoading, setTrendingLoading] = useState(true);
  // AI-generated regions and region-specific trending
  const [regions, setRegions] = useState([]);
  const [regionsLoading, setRegionsLoading] = useState(true);
  const [regionDestinations, setRegionDestinations] = useState(emptyTrendingDestinations);
  const [regionDestinationsLoading, setRegionDestinationsLoading] = useState(false);
  // Categories fetched from backend (configurable via System settings)
  const [categories, setCategories] = useState(['Mountains', 'Beaches', 'Cities', 'Nature']);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  // Lock destination if editing an existing itinerary (initialData provided).
  // initialData.prefill marks a suggested starting point instead — e.g. the
  // destination carried in from a blog article — which stays editable.
  const [destinationLocked, setDestinationLocked] = useState(
    !!initialData?.end_location && !initialData?.prefill
  );
  const isEditMode = !!initialData?.end_location && !initialData?.prefill;

  const [currentStep, setCurrentStep] = useState(1);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const TOTAL_STEPS = 3;
  const STEP_LABELS = ['Where & When', 'Vibe', 'Fine-tune'];

  const validateStep1 = () => {
    const newErrors = {};
    if (!startLocation.trim()) newErrors.startLocation = 'Start location is required';
    if (!endLocation.trim()) newErrors.endLocation = 'End location is required';
    if (!startDate || !endDate) newErrors.dates = 'Please select both start and end dates';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (selectedInterests.length === 0) newErrors.interests = 'Please select at least one interest';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const goNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setErrors({});
    setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goBack = () => {
    setErrors({});
    setCurrentStep((s) => Math.max(s - 1, 1));
    if (typeof window !== 'undefined') window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Voice support
  const voiceSupport = useVoiceSupport();

  // Fetch trending destinations on mount (uses pre-generated cache)
  const fetchTrending = useCallback(async () => {
    try {
      setTrendingLoading(true);
      // Use pre-generated trending places from database/Redis cache
      const response = await getTrendingPlacesByRegion('Europe');
      if (response.trending && Object.keys(response.trending).length > 0) {
        setTrendingDestinations(response.trending);
      }
    } catch (error) {
      console.log('Could not fetch trending destinations:', error);
    } finally {
      setTrendingLoading(false);
    }
  }, []);

  // Fetch regions from pre-generated cache
  const fetchRegions = useCallback(async () => {
    try {
      setRegionsLoading(true);
      const response = await getTrendingPlacesRegions();
      if (response.regions && response.regions.length > 0) {
        setRegions(response.regions);
      }
    } catch (error) {
      console.log('Could not fetch regions:', error);
    } finally {
      setRegionsLoading(false);
    }
  }, []);

  // Fetch categories from backend (configurable via System settings)
  const fetchCategories = useCallback(async () => {
    try {
      setCategoriesLoading(true);
      const response = await getTrendingPlacesCategories();
      if (response.categories && response.categories.length > 0) {
        setCategories(response.categories);
      }
    } catch (error) {
      console.log('Could not fetch categories:', error);
      // Keep default categories on error
    } finally {
      setCategoriesLoading(false);
    }
  }, []);

  // Fetch trending destinations for selected region (uses pre-generated cache)
  const fetchRegionDestinations = useCallback(async (regionName) => {
    try {
      setRegionDestinationsLoading(true);
      const response = await getTrendingPlacesByRegion(regionName);
      if (response.trending && Object.keys(response.trending).length > 0) {
        // Transform places to include proper image URLs
        const transformedTrending = {};
        for (const [category, places] of Object.entries(response.trending)) {
          transformedTrending[category] = places.map(place => ({
            ...place,
            // Use the image endpoint if it has a pre-generated image
            image: place.has_image ? getTrendingPlaceImageUrl(place.id) : (place.image_url || place.image)
          }));
        }
        setRegionDestinations(transformedTrending);
      }
    } catch (error) {
      console.log('Could not fetch region destinations:', error);
      setRegionDestinations(emptyTrendingDestinations);
    } finally {
      setRegionDestinationsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTrending();
    fetchRegions();
    fetchCategories();
  }, [fetchTrending, fetchRegions, fetchCategories]);

  // Fetch region destinations when region is selected
  useEffect(() => {
    if (selectedRegion) {
      fetchRegionDestinations(selectedRegion);
    }
  }, [selectedRegion, fetchRegionDestinations]);

  useEffect(() => {
    if (initialData) {
      setStartLocation(initialData.start_location || '');
      setEndLocation(initialData.end_location || '');
      setSelectedInterests(initialData.interests || []);
      setFoodPreference(initialData.food_preference || '');
      setStartDate(initialData.start_date || '');
      setEndDate(initialData.end_date || '');
      setTransportationMode(initialData.transportation_mode || '');
      setAvoidTollRoads(initialData.avoid_toll_roads || false);
      setPreferTollRoads(initialData.prefer_toll_roads || false);
      setScenicRoads(initialData.scenic_roads || false);
      // Convert waypoints array to city/days/dates format
      if (initialData.waypoints && Array.isArray(initialData.waypoints) && initialData.waypoints.length > 0) {
        const loadedWaypoints = initialData.waypoints
          .filter(wp => wp.name || wp)
          .map(wp => ({
            city: wp.name || wp,
            days: wp.days ? String(wp.days) : '',
            arrivalDate: wp.arrival_date || '',
            departureDate: wp.departure_date || ''
          }));
        if (loadedWaypoints.length > 0) {
          setWaypoints(loadedWaypoints);
        } else {
          setWaypoints([{ city: '', days: '', arrivalDate: '', departureDate: '' }]);
        }
      } else {
        setWaypoints([{ city: '', days: '', arrivalDate: '', departureDate: '' }]);
      }
    }
  }, [initialData]);

  const validateForm = () => {
    const newErrors = {};

    if (!startLocation.trim()) {
      newErrors.startLocation = 'Start location is required';
    }

    if (!endLocation.trim()) {
      newErrors.endLocation = 'End location is required';
    }

    if (selectedInterests.length === 0) {
      newErrors.interests = 'Please select at least one interest';
    }

    // Validate dates - both are mandatory
    if (!startDate || !endDate) {
      newErrors.dates = 'Please select both start and end dates';
    }

    // Validate waypoints: if arrival date is set, departure date is required
    const waypointErrors = [];
    waypoints.forEach((wp, index) => {
      if (wp.city && wp.city.trim()) {
        if (wp.arrivalDate && !wp.departureDate) {
          waypointErrors.push(`Stop ${index + 1} (${wp.city}): Departure date is required when arrival date is set`);
        }
      }
    });
    if (waypointErrors.length > 0) {
      newErrors.waypoints = waypointErrors;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const formData = {
      start_location: startLocation,
      end_location: endLocation,
      interests: selectedInterests,
      distance_radius: distanceRadius,
      distance_unit: distanceUnit,
    };

    if (initialData?.request_id) {
      formData.request_id = initialData.request_id;
    }

    if (foodPreference) {
      formData.food_preference = foodPreference;
    }

    // Dates are mandatory
    formData.start_date = startDate;
    formData.end_date = endDate;

    if (transportationMode) {
      formData.transportation_mode = transportationMode;
      formData.avoid_toll_roads = avoidTollRoads;
      formData.prefer_toll_roads = preferTollRoads;
      formData.scenic_roads = scenicRoads;
    }

    // Convert waypoints array to API format
    const validWaypoints = waypoints
      .filter(wp => wp.city && wp.city.trim())
      .map(wp => ({
        name: wp.city.trim(),
        days: wp.days ? parseInt(wp.days, 10) : null,
        arrival_date: wp.arrivalDate || null,
        departure_date: wp.departureDate || null
      }));

    if (validWaypoints.length > 0) {
      formData.waypoints = validWaypoints;
    }

    onSubmit(formData);
  };

  const toggleInterest = (value) => {
    if (selectedInterests.includes(value)) {
      setSelectedInterests(selectedInterests.filter(item => item !== value));
    } else {
      setSelectedInterests([...selectedInterests, value]);
    }
  };

  const handleCategoryClick = (category) => {
    // Toggle the category selection to show/hide trending destinations
    setSelectedCategory(selectedCategory === category.name ? null : category.name);
  };

  const handleDestinationClick = (destination, categoryName) => {
    // Set the destination name in the end location field and lock it
    setEndLocation(destination.name);
    setDestinationLocked(true);
    // Get category config and set its interests
    const config = getCategoryConfig(categoryName);
    if (config && config.interests) {
      setSelectedInterests(config.interests);
    }
    // Close the destination panel
    setSelectedCategory(null);
    // Clear any destination error
    if (errors.endLocation) {
      setErrors(prev => ({ ...prev, endLocation: '' }));
    }
  };

  // Calculate days between two dates (inclusive)
  const calculateDaysBetweenDates = (arrival, departure) => {
    if (!arrival || !departure) return '';
    const arrDate = new Date(arrival);
    const depDate = new Date(departure);
    if (isNaN(arrDate.getTime()) || isNaN(depDate.getTime())) return '';
    const diffTime = depDate.getTime() - arrDate.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24)) + 1; // +1 for inclusive
    return diffDays > 0 ? String(diffDays) : '';
  };

  // Waypoint handlers for dynamic city/days/dates rows
  const handleWaypointChange = (index, field, value) => {
    const newWaypoints = [...waypoints];
    newWaypoints[index] = { ...newWaypoints[index], [field]: value };

    // Clear all fields if city is empty
    if (field === 'city' && !value.trim()) {
      newWaypoints[index].days = '';
      newWaypoints[index].arrivalDate = '';
      newWaypoints[index].departureDate = '';
    }

    // Auto-calculate days when both dates are set
    if (field === 'arrivalDate' || field === 'departureDate') {
      const arrival = field === 'arrivalDate' ? value : newWaypoints[index].arrivalDate;
      const departure = field === 'departureDate' ? value : newWaypoints[index].departureDate;

      // Clear departure if it's before arrival
      if (field === 'arrivalDate' && departure && value > departure) {
        newWaypoints[index].departureDate = '';
        newWaypoints[index].days = '';
      } else if (arrival && departure) {
        newWaypoints[index].days = calculateDaysBetweenDates(arrival, departure);
      }
    }

    // Clear dates if days is manually changed (user wants manual mode)
    if (field === 'days' && value) {
      // Only clear dates if user is actively typing (not auto-calculated)
      const currentCalculated = calculateDaysBetweenDates(
        newWaypoints[index].arrivalDate,
        newWaypoints[index].departureDate
      );
      if (value !== currentCalculated) {
        newWaypoints[index].arrivalDate = '';
        newWaypoints[index].departureDate = '';
      }
    }

    setWaypoints(newWaypoints);
  };

  const addWaypoint = (index) => {
    const newWaypoints = [...waypoints];
    newWaypoints.splice(index + 1, 0, { city: '', days: '', arrivalDate: '', departureDate: '' });
    setWaypoints(newWaypoints);
  };

  const removeWaypoint = (index) => {
    if (waypoints.length > 1) {
      const newWaypoints = waypoints.filter((_, i) => i !== index);
      setWaypoints(newWaypoints);
    } else {
      // If it's the last one, just clear it
      setWaypoints([{ city: '', days: '', arrivalDate: '', departureDate: '' }]);
    }
  };

  // Get minimum arrival date for a waypoint based on previous waypoint's departure or trip start
  const getMinArrivalDate = (index) => {
    if (index > 0 && waypoints[index - 1].departureDate) {
      return waypoints[index - 1].departureDate;
    }
    return startDate || new Date().toISOString().split('T')[0];
  };

  // Get maximum departure date for a waypoint (trip end date)
  const getMaxDepartureDate = () => {
    return endDate || '';
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="bg-gradient-sunset rounded-2xl p-8 mb-6 text-white shadow-elevated">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold mb-2 flex items-center gap-3">
              <FaPlane className="text-4xl" />
              Plan Your Perfect Trip
            </h1>
            <p className="text-white/90 text-lg">
              Tell us about your dream destination and we'll create a personalized itinerary
            </p>
          </div>
          <PageGuide pageKey="create-itinerary" variant="light" />
        </div>
      </div>

      {/* Important Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-8 flex items-start gap-3">
        <FaInfoCircle className="text-amber-500 text-lg flex-shrink-0 mt-0.5" />
        <div>
          {isEditMode ? (
            <>
              <p className="text-amber-800 text-sm font-medium">
                You're modifying an existing itinerary. The destination cannot be changed.
              </p>
              <p className="text-amber-600 text-xs mt-1">
                To travel to a different destination, please create a new itinerary.
              </p>
            </>
          ) : (
            <>
              <p className="text-amber-800 text-sm font-medium">
                Choose your destination carefully - it cannot be changed after your itinerary is created.
              </p>
              <p className="text-amber-600 text-xs mt-1">
                You can always create a new itinerary for a different destination.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Quick Start - AI-Powered Region & Category Selection */}
      <div className="bg-white rounded-xl shadow-medium p-6 mb-8">
        <h2 className="text-lg font-bold text-neutral-800 mb-4 text-center">Quick Start - Choose Your Adventure</h2>

        {/* Step 1: Region Selection (show when no region selected) */}
        {!selectedRegion && (
          <div className="animate-fade-in">
            <p className="text-center text-neutral-500 text-sm mb-4">
              <FaGlobe className="inline mr-1" />
              Select a region to explore trending destinations
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {regionsLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-neutral-500">
                  <FaRocket className="animate-pulse text-primary-400" />
                  <span>AI is preparing travel regions...</span>
                </div>
              ) : regions.length === 0 ? (
                <div className="text-center py-8 text-neutral-400">
                  <span>No regions available. Please try again later.</span>
                </div>
              ) : regions.map((region, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelectedRegion(region.name)}
                  disabled={loading}
                  className={`flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r ${region.color || 'from-blue-500 to-indigo-600'} text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed`}
                  style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                >
                  <span className="text-lg drop-shadow">{region.emoji || '🌍'}</span>
                  <span className="font-semibold drop-shadow">{region.name}</span>
                </button>
              ))}
            </div>
            {regions.length > 0 && regions[0].trending_note && (
              <p className="text-center text-neutral-400 text-xs mt-4 italic">
                AI-powered recommendations updated in real-time
              </p>
            )}
          </div>
        )}

        {/* Step 2: Category Selection (show when region selected but no category) */}
        {selectedRegion && !selectedCategory && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => setSelectedRegion(null)}
                className="flex items-center gap-1 text-neutral-500 hover:text-neutral-700 transition-colors text-sm"
              >
                <FaChevronLeft />
                <span>Back to regions</span>
              </button>
              <div className="flex items-center gap-2">
                <FaGlobe className="text-primary-500" />
                <span className="font-semibold text-neutral-800">{selectedRegion}</span>
              </div>
            </div>
            <p className="text-center text-neutral-500 text-sm mb-4">What type of destination are you looking for?</p>
            <div className="flex flex-wrap justify-center gap-3">
              {categoriesLoading ? (
                <div className="flex items-center justify-center gap-2 py-4 text-neutral-500">
                  <FaRocket className="animate-pulse text-primary-400" />
                  <span>Loading categories...</span>
                </div>
              ) : categories.map((categoryName, index) => {
                const config = getCategoryConfig(categoryName);
                const CategoryIcon = config.icon;
                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleCategoryClick({ name: categoryName, ...config })}
                    disabled={loading}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r ${config.color} text-white shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed`}
                    style={{ textShadow: '0 1px 2px rgba(0,0,0,0.3)' }}
                  >
                    <CategoryIcon className="text-lg" />
                    <span className="font-semibold">{categoryName}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 3: Trending Destinations Display (scrollable grid) */}
        {selectedCategory && (
          <div className="animate-fade-in">
            <div className="flex items-center justify-between mb-4">
              <button
                type="button"
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-1 text-neutral-500 hover:text-neutral-700 transition-colors text-sm"
              >
                <FaChevronLeft />
                <span>Back to categories</span>
              </button>
              <div className="flex items-center gap-2">
                <FaFire className="text-xl text-orange-500" />
                <h3 className="text-lg font-bold text-neutral-800">
                  {selectedCategory} in {selectedRegion || 'Worldwide'}
                </h3>
              </div>
              <button
                type="button"
                onClick={() => {
                  setSelectedCategory(null);
                  setSelectedRegion(null);
                }}
                className="flex items-center gap-1 text-neutral-500 hover:text-neutral-700 transition-colors text-sm"
              >
                <FaTimes />
                <span>Close</span>
              </button>
            </div>

            {/* Scrollable destination grid - 4 per row, max 2 rows visible, scrollable */}
            <div className="max-h-[360px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-primary-300 scrollbar-track-neutral-100">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 p-1">
                {(regionDestinationsLoading || trendingLoading) ? (
                  <div className="col-span-4 text-center py-8 text-neutral-500">
                    <div className="flex items-center justify-center gap-2">
                      <FaRocket className="animate-pulse text-primary-400" />
                      <span>Loading trending destinations in {selectedRegion}...</span>
                    </div>
                  </div>
                ) : ((selectedRegion ? regionDestinations[selectedCategory] : trendingDestinations[selectedCategory]) || []).length === 0 ? (
                  <div className="col-span-4 text-center py-8 text-neutral-400">
                    <span>No trending destinations available for {selectedCategory} in {selectedRegion || 'this region'}. Try another category.</span>
                  </div>
                ) : ((selectedRegion ? regionDestinations[selectedCategory] : trendingDestinations[selectedCategory]) || []).map((place, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleDestinationClick(place, selectedCategory)}
                    disabled={loading}
                    className="group bg-white rounded-xl shadow-soft hover:shadow-elevated overflow-hidden transition-all duration-300 hover:-translate-y-1 text-left disabled:opacity-50 disabled:cursor-not-allowed border border-neutral-100"
                  >
                    {/* Image Container */}
                    <div className="relative h-32 sm:h-36 overflow-hidden">
                      <img
                        src={place.image_url || place.image || (place.id ? `/api/trending-places/image/${place.id}` : '')}
                        alt={place.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                        loading="lazy"
                        onError={(e) => {
                          e.target.src = getCategoryFallbackImage(selectedCategory);
                        }}
                      />
                      {/* Trend Badge */}
                      <div className="absolute top-2 left-2 flex flex-col gap-1">
                        <span className="inline-flex items-center gap-1 bg-white/90 backdrop-blur-sm text-xs font-semibold text-orange-600 px-2 py-0.5 rounded-full">
                          <FaFire className="text-orange-500 text-xs" />
                          {place.trend}
                        </span>
                        {place.safety_warning && (
                          <span className="inline-flex items-center gap-1 bg-amber-100/95 backdrop-blur-sm text-xs font-semibold text-amber-700 px-2 py-0.5 rounded-full">
                            <FaExclamationTriangle className="text-amber-600 text-xs" />
                            {place.safety_warning}
                          </span>
                        )}
                      </div>
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
                      {/* Location Name on Image */}
                      <div className="absolute bottom-2 left-2 right-2">
                        <div className="flex items-center gap-1 text-white">
                          <FaMapMarkerAlt className="text-xs text-secondary-400" />
                          <span className="font-semibold text-sm drop-shadow-lg truncate">{place.name}</span>
                        </div>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-2 sm:p-3">
                      <p className="text-neutral-600 text-xs leading-relaxed line-clamp-2 mb-2">
                        {place.description}
                      </p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-neutral-400 hidden sm:inline">Click to select</span>
                        <span className="flex items-center gap-1 text-primary-600 font-medium text-xs group-hover:gap-2 transition-all">
                          Select
                          <FaChevronRight className="text-xs" />
                        </span>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
            <p className="text-center text-neutral-400 text-xs mt-3">
              Scroll to see more destinations
            </p>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Progress bar */}
        <div className="bg-white rounded-xl shadow-medium p-5">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-semibold text-neutral-700">
              Step {currentStep} of {TOTAL_STEPS} · {STEP_LABELS[currentStep - 1]}
            </p>
            <p className="text-sm font-semibold text-primary-600">
              {Math.round((currentStep / TOTAL_STEPS) * 100)}% complete
            </p>
          </div>
          <div className="relative h-2 bg-neutral-200 rounded-full overflow-hidden">
            <div
              className="absolute top-0 left-0 h-full bg-gradient-to-r from-primary-500 via-accent-500 to-secondary-500 transition-all duration-300"
              style={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-3 text-xs">
            {STEP_LABELS.map((label, i) => (
              <span
                key={label}
                className={
                  i + 1 === currentStep
                    ? 'text-primary-600 font-semibold'
                    : i + 1 < currentStep
                    ? 'text-neutral-700 font-medium'
                    : 'text-neutral-400'
                }
              >
                {i + 1}. {label}
              </span>
            ))}
          </div>
        </div>

        {/* Step 1: Where & When */}
        {currentStep === 1 && (
          <div className="bg-white rounded-xl shadow-medium p-6 space-y-5 animate-fade-in">
            <h2 className="text-xl font-bold text-neutral-800 flex items-center gap-2 pb-3 border-b border-neutral-300">
              <FaMapMarkerAlt className="text-primary-500" />
              Where & When
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Start Location */}
              <div>
                <LocationInput
                  id="startLocation"
                  label="Start Location"
                  value={startLocation}
                  onChange={setStartLocation}
                  onSelect={(location) => {
                    if (location) {
                      setStartLocation(`${location.name}${location.state ? `, ${location.state}` : ''}${location.country ? `, ${location.country}` : ''}`);
                    }
                  }}
                  placeholder="New York, USA"
                  icon={FaMapMarkerAlt}
                  iconColor="text-primary-500"
                  error={errors.startLocation}
                  disabled={loading}
                  voiceInput={voiceSupport.speechRecognition ? (
                    <VoiceInputInline disabled={loading} className="flex-shrink-0" />
                  ) : null}
                />
              </div>

              {/* End Location / Destination */}
              <div>
                {destinationLocked ? (
                  <>
                    <label htmlFor="endLocation" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                      <FaPlane className="text-secondary-500" />
                      Destination
                      <span className={`ml-2 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        isEditMode
                          ? 'text-amber-600 bg-amber-100'
                          : 'text-green-600 bg-green-100'
                      }`}>
                        <FaLock className="text-xs" />
                        {isEditMode ? 'Cannot be changed' : 'Selected'}
                      </span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="endLocation"
                        value={endLocation}
                        readOnly
                        className={`w-full px-4 py-3 rounded-lg border-2 transition-all ${
                          isEditMode
                            ? 'border-amber-500 bg-amber-50 text-amber-800 font-semibold cursor-not-allowed'
                            : 'border-green-500 bg-green-50 text-green-800 font-semibold cursor-not-allowed'
                        } outline-none`}
                        disabled={loading}
                      />
                      {!isEditMode && (
                        <button
                          type="button"
                          onClick={() => {
                            setDestinationLocked(false);
                            setEndLocation('');
                          }}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-red-500 p-2 transition-colors"
                          title="Change destination"
                        >
                          <FaTimes />
                        </button>
                      )}
                    </div>
                  </>
                ) : (
                  <LocationInput
                    id="endLocation"
                    label="Destination"
                    value={endLocation}
                    onChange={setEndLocation}
                    onSelect={(location) => {
                      if (location) {
                        setEndLocation(`${location.name}${location.state ? `, ${location.state}` : ''}${location.country ? `, ${location.country}` : ''}`);
                        setDestinationLocked(true);
                        if (errors.endLocation) {
                          setErrors(prev => ({ ...prev, endLocation: '' }));
                        }
                      }
                    }}
                    placeholder="Paris, France"
                    icon={FaPlane}
                    iconColor="text-secondary-500"
                    error={errors.endLocation}
                    disabled={loading}
                    voiceInput={voiceSupport.speechRecognition ? (
                      <VoiceInputInline disabled={loading} className="flex-shrink-0" />
                    ) : null}
                  />
                )}
              </div>
            </div>

            {/* Travel Dates */}
            <div className="mt-5">
              <label className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                <FaCalendarAlt className="text-accent-500" />
                Dates
                <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selectsRange={true}
                startDate={startDate ? new Date(startDate + 'T00:00:00') : null}
                endDate={endDate ? new Date(endDate + 'T00:00:00') : null}
                onChange={(dates) => {
                  const [start, end] = dates;
                  setStartDate(start ? start.toISOString().split('T')[0] : '');
                  setEndDate(end ? end.toISOString().split('T')[0] : '');
                }}
                minDate={new Date()}
                monthsShown={2}
                dateFormat="MMM d, yyyy"
                placeholderText="Select start and end dates"
                className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-accent-500 focus:ring-4 focus:ring-accent-100 outline-none transition-all disabled:opacity-50 cursor-pointer"
                wrapperClassName="w-full"
                disabled={loading}
                isClearable={true}
                showPopperArrow={false}
                calendarClassName="date-range-calendar"
              />
              {errors.dates && (
                <p className="text-red-600 text-sm mt-1">{errors.dates}</p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Vibe */}
        {currentStep === 2 && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white rounded-xl shadow-medium p-6 space-y-5">
              <h2 className="text-xl font-bold text-neutral-800 flex items-center gap-2 pb-3 border-b border-neutral-300">
                <FaCamera className="text-primary-500" />
                What Interests You?
              </h2>
              <p className="text-sm text-neutral-500 -mt-2">Pick a few — these help us decide what to include each day.</p>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {interestOptions.map((interest) => {
                  const Icon = interest.icon;
                  const isSelected = selectedInterests.includes(interest.value);
                  return (
                    <button
                      key={interest.value}
                      type="button"
                      onClick={() => !loading && toggleInterest(interest.value)}
                      disabled={loading}
                      className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-all duration-200 ${
                        isSelected
                          ? 'border-primary-500 bg-primary-50 text-primary-700 shadow-travel scale-105'
                          : 'border-neutral-300 hover:border-primary-300 hover:bg-primary-50/50'
                      } disabled:opacity-50 disabled:cursor-not-allowed`}
                    >
                      <Icon className={`text-2xl ${isSelected ? 'text-primary-500' : 'text-neutral-600'}`} />
                      <span className={`text-sm font-medium ${isSelected ? 'text-primary-700' : 'text-neutral-700'}`}>
                        {interest.label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {errors.interests && (
                <p className="text-red-600 text-sm mt-2">{errors.interests}</p>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-medium p-6">
              <h3 className="text-lg font-bold text-neutral-800 mb-1">Preferences</h3>
              <p className="text-sm text-neutral-500 mb-4">Optional — leave blank if it doesn't matter.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="foodPreference" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                    <FaUtensils className="text-primary-500" />
                    Dietary Restrictions
                  </label>
                  <select
                    id="foodPreference"
                    value={foodPreference}
                    onChange={(e) => setFoodPreference(e.target.value)}
                    disabled={loading}
                    className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-primary-500 focus:ring-4 focus:ring-primary-100 outline-none transition-all disabled:opacity-50 bg-white"
                  >
                    <option value="">No Preference</option>
                    <option value="Vegetarian">Vegetarian</option>
                    <option value="Vegan">Vegan</option>
                    <option value="Jain">Jain</option>
                    <option value="Halal">Halal</option>
                    <option value="Kosher">Kosher</option>
                    <option value="Gluten-Free">Gluten-Free</option>
                    <option value="Pescatarian">Pescatarian</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="transportationMode" className="flex items-center gap-2 text-sm font-semibold text-neutral-700 mb-2">
                    <FaCar className="text-secondary-500" />
                    Transportation
                  </label>
                  <select
                    id="transportationMode"
                    value={transportationMode}
                    onChange={(e) => setTransportationMode(e.target.value)}
                    disabled={loading}
                    className="w-full px-4 py-3 rounded-lg border-2 border-neutral-300 focus:border-secondary-500 focus:ring-4 focus:ring-secondary-100 outline-none transition-all disabled:opacity-50 bg-white"
                  >
                    <option value="">No Preference</option>
                    <option value="Driving">Driving</option>
                    <option value="Flying">Flying</option>
                    <option value="Train">Train</option>
                    <option value="Bus">Bus</option>
                    <option value="Walking">Walking</option>
                    <option value="Cycling">Cycling</option>
                    <option value="Mixed">Mixed</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Step 3: Fine-tune */}
        {currentStep === 3 && (
          <div className="space-y-6 animate-fade-in">
            <div className="bg-white rounded-xl shadow-medium p-6 space-y-4">
              <div>
                <h2 className="text-xl font-bold text-neutral-800 flex items-center gap-2">
                  <FaDirections className="text-primary-500" />
                  Fine-tune
                  <span className="text-sm font-normal text-neutral-500">(optional)</span>
                </h2>
                <p className="text-sm text-neutral-500 mt-1">
                  Add stops along the way or driving preferences — or skip and generate.
                </p>
              </div>

              {/* Distance Radius */}
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                <label className="flex items-center gap-2 text-sm font-semibold text-blue-800 mb-3">
                  <FaRuler className="text-blue-600" />
                  Maximum Distance for Attractions
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="5"
                    max="500"
                    value={distanceRadius}
                    onChange={(e) => setDistanceRadius(Math.max(5, Math.min(500, parseInt(e.target.value) || 50)))}
                    disabled={loading}
                    className="w-24 px-3 py-2.5 rounded-lg border-2 border-blue-300 bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-100 outline-none disabled:opacity-50 text-center font-semibold"
                  />
                  <span className="text-blue-700 font-medium">{distanceUnit}</span>
                  <span className="text-blue-600 text-sm">from destination</span>
                </div>
                <p className="text-xs text-blue-600 mt-2">
                  Keep attractions within a comfortable commuting distance. Adjust based on your trip style.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setShowAdvanced((v) => !v)}
                className="w-full flex items-center justify-between gap-2 px-4 py-3 rounded-lg border-2 border-neutral-200 hover:border-primary-300 transition-colors"
              >
                <span className="font-semibold text-neutral-700">
                  {showAdvanced ? 'Hide advanced options' : 'Show advanced options'}
                </span>
                <FaChevronRight className={`text-neutral-400 transition-transform ${showAdvanced ? 'rotate-90' : ''}`} />
              </button>

              {showAdvanced && (
                <div className="space-y-5 animate-slide-down">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                    <label className="flex items-center gap-2 text-sm font-semibold text-emerald-800 mb-3">
                      <FaDirections className="text-emerald-600" />
                      Stops Along the Way
                      <span className="text-xs font-normal text-emerald-600">(optional)</span>
                    </label>
                    <div className="space-y-2">
                      {waypoints.map((waypoint, index) => (
                        <div key={index} className="flex items-center gap-2 flex-wrap">
                          <div className="flex items-center gap-1 flex-1 min-w-[180px]">
                            <input
                              type="text"
                              value={waypoint.city}
                              onChange={(e) => handleWaypointChange(index, 'city', e.target.value)}
                              placeholder="City or place (e.g., Barcelona, Bali)"
                              className="flex-1 px-4 py-2.5 rounded-lg border-2 border-emerald-300 bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                              disabled={loading}
                            />
                            {voiceSupport.speechRecognition && (
                              <VoiceInputInline
                                onTranscript={(text) => handleWaypointChange(index, 'city', text)}
                                disabled={loading}
                                className="flex-shrink-0"
                              />
                            )}
                          </div>
                          <DatePicker
                            selectsRange={true}
                            startDate={waypoint.arrivalDate ? new Date(waypoint.arrivalDate + 'T00:00:00') : null}
                            endDate={waypoint.departureDate ? new Date(waypoint.departureDate + 'T00:00:00') : null}
                            onChange={(dates) => {
                              const [arrival, departure] = dates;
                              const newWaypoints = [...waypoints];
                              newWaypoints[index] = {
                                ...newWaypoints[index],
                                arrivalDate: arrival ? arrival.toISOString().split('T')[0] : '',
                                departureDate: departure ? departure.toISOString().split('T')[0] : ''
                              };
                              setWaypoints(newWaypoints);
                            }}
                            minDate={startDate ? new Date(startDate + 'T00:00:00') : new Date()}
                            maxDate={endDate ? new Date(endDate + 'T00:00:00') : null}
                            shouldCloseOnSelect={false}
                            monthsShown={2}
                            dateFormat="MMM d, yyyy"
                            placeholderText="Select dates (optional)"
                            className="flex-1 min-w-[220px] px-3 py-2.5 rounded-lg border-2 border-emerald-300 bg-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100 outline-none disabled:opacity-50 disabled:cursor-not-allowed text-sm cursor-pointer"
                            wrapperClassName="flex-1 min-w-[220px]"
                            disabled={loading || !waypoint.city.trim()}
                            isClearable={true}
                            showPopperArrow={false}
                            calendarClassName="date-range-calendar waypoint-calendar"
                          />
                          <button
                            type="button"
                            onClick={() => addWaypoint(index)}
                            disabled={loading}
                            className="p-2.5 rounded-lg bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Add another stop"
                          >
                            <FaPlus className="w-4 h-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => removeWaypoint(index)}
                            disabled={loading}
                            className="p-2.5 rounded-lg bg-white text-neutral-600 border border-emerald-300 hover:bg-red-100 hover:text-red-600 hover:border-red-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Remove this stop"
                          >
                            <FaTimes className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-emerald-600 mt-3">
                      Add places you want to visit along the way with optional arrival and departure dates.
                    </p>
                    {errors.waypoints && (
                      <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                        {errors.waypoints.map((err, idx) => (
                          <p key={idx} className="text-red-600 text-sm">{err}</p>
                        ))}
                      </div>
                    )}
                  </div>

                  {transportationMode === 'Driving' ? (
                    <div className="bg-secondary-50 rounded-lg p-4 space-y-3">
                      <h3 className="font-semibold text-neutral-800 mb-3">Driving Preferences</h3>
                      <div className="space-y-2">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={avoidTollRoads}
                            onChange={(e) => {
                              setAvoidTollRoads(e.target.checked);
                              if (e.target.checked) setPreferTollRoads(false);
                            }}
                            disabled={loading}
                            className="w-5 h-5 rounded border-2 border-neutral-300 text-secondary-500 focus:ring-2 focus:ring-secondary-400 transition-colors cursor-pointer disabled:opacity-50"
                          />
                          <span className="font-medium text-neutral-700">Avoid Toll Roads</span>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={preferTollRoads}
                            onChange={(e) => {
                              setPreferTollRoads(e.target.checked);
                              if (e.target.checked) setAvoidTollRoads(false);
                            }}
                            disabled={loading}
                            className="w-5 h-5 rounded border-2 border-neutral-300 text-secondary-500 focus:ring-2 focus:ring-secondary-400 transition-colors cursor-pointer disabled:opacity-50"
                          />
                          <span className="font-medium text-neutral-700">Prefer Toll Roads</span>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={scenicRoads}
                            onChange={(e) => setScenicRoads(e.target.checked)}
                            disabled={loading}
                            className="w-5 h-5 rounded border-2 border-neutral-300 text-secondary-500 focus:ring-2 focus:ring-secondary-400 transition-colors cursor-pointer disabled:opacity-50"
                          />
                          <span className="font-medium text-neutral-700">Prefer Scenic Routes</span>
                        </label>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-neutral-500 italic px-1">
                      Tip: pick <strong>Driving</strong> as your transport mode in step 2 to add toll/scenic preferences here.
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Subscription Limit Error */}
            {subscriptionLimitError && (
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-300 rounded-xl p-5">
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <FaExclamationTriangle className="text-amber-600 text-xl" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-amber-800 text-lg mb-1">
                      {subscriptionLimitError.limit > 0 ? 'Monthly limit reached' : 'Unlock AI trip planning'}
                    </h3>
                    <p className="text-amber-700 mb-3">
                      {subscriptionLimitError.message}
                    </p>
                    {subscriptionLimitError.limit > 0 && (
                      <div className="flex items-center gap-2 text-sm text-amber-600 mb-3">
                        <span className="bg-amber-200 px-2 py-1 rounded-full font-medium">
                          {subscriptionLimitError.used} / {subscriptionLimitError.limit}{' '}
                          {subscriptionLimitError.limit === 1 ? 'itinerary' : 'itineraries'} used
                        </span>
                      </div>
                    )}
                    {onViewPricing && (
                      <button
                        type="button"
                        onClick={onViewPricing}
                        className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-5 py-2 rounded-lg font-semibold hover:shadow-lg hover:scale-105 transition-all"
                      >
                        <FaRocket />
                        Upgrade Your Plan
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading || subscriptionLimitError}
              className="w-full bg-gradient-primary text-white py-4 px-8 rounded-xl font-bold text-lg shadow-travel hover:shadow-elevated hover:scale-105 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-3">
                  <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                  Generating Your Perfect Itinerary...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-3">
                  <FaPlane className="text-2xl" />
                  Generate Itinerary
                </span>
              )}
            </button>
          </div>
        )}

        {/* Step navigation */}
        <div className="flex items-center justify-between gap-3">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={goBack}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-neutral-700 bg-white border-2 border-neutral-200 hover:border-neutral-400 hover:bg-neutral-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FaChevronLeft /> Back
            </button>
          ) : (
            <div />
          )}

          {currentStep < TOTAL_STEPS && (
            <button
              type="button"
              onClick={goNext}
              disabled={loading}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-white bg-gradient-to-r from-primary-500 to-accent-500 shadow-md hover:shadow-glow-primary transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next <FaChevronRight />
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default ItineraryForm;
